/* eslint-disable react/jsx-key */

import React from 'react'
import { Route, IndexRedirect, IndexRoute } from 'react-router'

import App from './components/App'
import Login from './components/auth/Login'
import InitialPasswordForm from './components/auth/InitialPasswordForm'

/**
 * Password Resets
 */
import PasswordResetRequest from './components/auth/PasswordResetRequest'
import PasswordResetSubmitted from './components/auth/PasswordResetSubmitted'
import PasswordResetSuccess from './components/auth/PasswordResetSuccess'
import PasswordResetForm from './components/auth/PasswordResetForm'

import Activating from './components/Activating'
import Provision from './components/Provision'
import Zapier from './components/auth/Zapier'
import Agreements from './components/Agreements'
import WebsiteWrapper from './components/WebsiteWrapper'

import Websites from './components/sections/Websites'
import BillingUpgrade from './components/sections/Billing/Upgrade'
import BillingActivate from './components/sections/Billing/Activate'
import Billing from './components/sections/Billing'
import TwoToThree from './components/sections/Billing/TwoToThree'
import Users from './components/sections/Users'
import UserDetails from './components/sections/users/Details'
import UserDelete from './components/sections/users/Delete'
import Integrations from './components/sections/Integrations'
import IntegrationsAdd from './components/sections/Integrations/Add'
import IntegrationsEdit from './components/sections/Integrations/Edit'

import Welcome from './components/sections/website/Welcome'
import SearchLibrary from './components/sections/website/SearchLibrary'
import AddSearch from './components/sections/website/SearchLibrary/Add'
import EditSearch from './components/sections/website/SearchLibrary/Edit'
import DuplicateSearch from './components/sections/website/SearchLibrary/Duplicate'
import RecommendedSearches from './components/sections/website/RecommendedSearches'
import AddRecommendedSearch from './components/sections/website/RecommendedSearches/Add'
import RecommendedListings from './components/sections/website/RecommendedListings'
import AddRecommendedListing from './components/sections/website/RecommendedListings/Add'
import Hotsheets from './components/sections/website/Hotsheets'
import AddHotsheet from './components/sections/website/Hotsheets/Add'
import EditHotsheet from './components/sections/website/Hotsheets/Edit'
import WebsiteLeads from './components/sections/website/Leads'
import WebsiteLeadDetails from './components/sections/website/Leads/Details'
import WebsiteLeadEditSearch from './components/sections/website/Leads/EditSearch'
import WebsiteLeadInvite from './components/sections/website/Leads/Invite'
import WebsiteMessages from './components/sections/website/Messages'
import WebsiteMessageDetails from './components/sections/website/Messages/Details'
import Shortcodes from './components/sections/website/Shortcodes'
import GeneralSettings from './components/sections/website/settings/General'
import EmailSettings from './components/sections/website/settings/Email'
import DesignSettings from './components/sections/website/settings/Design'
import DisplaySettings from './components/sections/website/settings/Display'
import RegistrationSettings from './components/sections/website/settings/Registration'
import SeoSettings from './components/sections/website/settings/SEO'
import SearchSettings from './components/sections/website/settings/Search'
import LeadRouting from './components/sections/website/settings/LeadRouting'
import PrivacySettings from './components/sections/website/settings/Privacy'

const notAuthed = auth => {
  return (nextState, replace) => {
    let { location: { state } } = nextState
    if (auth.loggedIn) replace(state && state.return_to || { pathname: '/' })
  }
}

const notActivated = auth => {
  return (nextState, replace) => {
    if (!auth.loggedIn) replace({ pathname: '/login' })
    else if (auth.activated) replace('/')
  }
}

const ensureNoDuplicateActivation = auth => {
  return (nextState, replace) => {
    if (auth.user.trial_info === null) {
      replace('/billing')
    } else {
      if (auth.user.trial_info.has_subscribed) replace('/billing')
    }
  }
}

// TODO: IDEALLY we would just route /subscribe to /billing and handle it all there. Or, chain multiple onEnter methods
const redirectSubscribeToAppropriateNextStep = auth => {
  return (nextState, replace) => {
    if (auth.user.trial_info && !auth.user.trial_info.has_subscribed) replace('/billing/activate')
    replace('/billing')
  }
}

const requireSubscription = auth => {
  return (nextState, replace) => {
    if (auth.user.trial_info && !auth.user.trial_info.has_subscribed) replace('/billing/activate')
  }
}

const enforceBillingUpdate = auth => {
  return (nextState, replace) => {
    if (auth.user.impersonated) return
    if (auth.user.license_needs_billing_update == 'hard' && nextState.location.pathname != '/billing') replace('/billing')
    if (auth.user.trial_info && auth.user.trial_info.expired && nextState.location.pathname != '/billing/activate') replace('/billing/activate')
  }
}

// Bootstrap Auth
const requireAuth = auth => {
  return (nextState, replace) => {
    if (!auth.loggedIn) replace({ pathname: '/login', state: { return_to: nextState.location } })
    else if (!auth.activated) replace('/activating')
    else if (nextState.location.pathname != '/billing/activate' && auth.user.trial_info && auth.user.trial_info.expired && !auth.user.trial_info.has_subscribed) replace('/billing/activate')
  }
}

const requireUndecided = auth => {
  return (nextState, replace) => {
    const { migration_status, admin } = auth.user
    if (migration_status !== 'migrated_undecided' || !admin) {
      return replace({ pathname: '/' })
    }
  }
}

const routes = auth => [
  <Route path="/login" component={Login} onEnter={notAuthed(auth)} />,
  <Route path="/login/initial-password" component={InitialPasswordForm} onEnter={notAuthed(auth)} />,

  /**
   * Password Resets
   */
  <Route path="/login/password-reset/request" component={PasswordResetRequest} />,
  <Route path="/login/password-reset/submitted" component={PasswordResetSubmitted} />,
  <Route path="/login/password-reset/success" component={PasswordResetSuccess} />,
  <Route path="/login/password-reset/:reset_token" component={PasswordResetForm} />,

  <Route path="/activating" component={Activating} onEnter={notActivated(auth)} />,
  <Route path="/subscribe" component={BillingActivate} onEnter={redirectSubscribeToAppropriateNextStep(auth)} />,
  <Route path="/provision/:installation_uuid" component={Provision} onEnter={requireAuth(auth)} />,
  <Route path="/zapier" component={Zapier} onEnter={requireAuth(auth)} />,

  <Route path="/" component={App} onEnter={requireAuth(auth)}>
    <IndexRedirect to="websites" />
    <Route path="websites" component={Websites} onEnter={enforceBillingUpdate(auth)} />
    <Route path="billing">
      <IndexRoute component={Billing} onEnter={requireSubscription(auth)} />
      <Route path='activate' component={BillingActivate} onEnter={ensureNoDuplicateActivation(auth)} />
      <Route path='upgrade' component={BillingUpgrade} onEnter={requireSubscription(auth)} />
      <Route path='plan' component={BillingUpgrade} />
    </Route>
    <Route path="users" component={Users} onEnter={enforceBillingUpdate(auth)}>
      <Route path="delete/:user_id" component={UserDelete} />
      <Route path=":user_id" component={UserDetails} />
    </Route>
    <Route path="integrations" onEnter={enforceBillingUpdate(auth)}>
      <IndexRoute component={Integrations} />
      <Route path="add/:type" component={IntegrationsAdd} />
      <Route path="edit/:id" component={IntegrationsEdit} />
    </Route>
    <Route path="confirm-plan" component={TwoToThree} onEnter={requireUndecided(auth)} />
    <Route path="agreements" component={Agreements} onEnter={requireAuth(auth)} />,

    <Route path='website/:website_id' component={WebsiteWrapper} onEnter={enforceBillingUpdate(auth)}>
      <IndexRedirect to='leads' />
      <Route path='welcome' component={Welcome} />
      <Route path='leads'>
        <IndexRoute component={WebsiteLeads} />
        <Route path='invite' component={WebsiteLeadInvite} />
        <Route path=':lead_id/edit-search/:search_id' component={WebsiteLeadEditSearch} />
        <Route path=':lead_id' component={WebsiteLeadDetails} />
      </Route>
      <Route path='messages'>
        <IndexRoute component={WebsiteMessages} />
        <Route path=':message_id' component={WebsiteMessageDetails} />
      </Route>
      <Route path='hotsheets'>
        <IndexRoute component={Hotsheets} />
        <Route path='add' component={AddHotsheet} />
        <Route path='edit/:hotsheetId' component={EditHotsheet} />
      </Route>
      <Route path='recommended/listings'>
        <IndexRoute component={RecommendedListings} />
        <Route path='add' component={AddRecommendedListing} />
      </Route>
      <Route path='recommended/searches'>
        <IndexRoute component={RecommendedSearches} />
        <Route path='add(/:lead_id)' component={AddRecommendedSearch} />
      </Route>
      <Route path='searches'>
        <IndexRoute component={SearchLibrary} />
        <Route path='add' component={AddSearch} />
        <Route path='edit/:searchId' component={EditSearch} />
        <Route path='duplicate/:searchId' component={DuplicateSearch} />
      </Route>
      <Route path='shortcodes'>
        <IndexRoute component={Shortcodes} />
      </Route>
      <Route path='settings'>
        <Route path='general' component={GeneralSettings} />
        <Route path='email' component={EmailSettings} />
        <Route path='design' component={DesignSettings} />
        <Route path='display' component={DisplaySettings} />
        <Route path='registration' component={RegistrationSettings} />
        <Route path='seo' component={SeoSettings} />
        <Route path='search' component={SearchSettings} />
        <Route path='lead-routing' component={LeadRouting} />
        <Route path='privacy' component={PrivacySettings} />
      </Route>
    </Route>
  </Route>
]

export default routes



// WEBPACK FOOTER //
// client/routes.js